import vuetify from '@/plugins/vuetify';
import Vue from 'vue';
import Component from 'vue-class-component';
import App from './App.vue';
import './directives';
import './filters';
import i18n from './i18n';
import router from './router';
import Auth from './services/auth/auth';
import store from './store';
import { PERMISSION, hasPermission } from '@/permissions';

Vue.config.productionTip = false;

// Route name → required permission key. Routes absent from this map have no role gate.
// Defined once at module scope (not per navigation). Events sub-routes all share ROUTE_EVENTS.
const ROUTE_PERMISSIONS: { [name: string]: PERMISSION } = {
	Menus: PERMISSION.ROUTE_MENUS,
	Customize: PERMISSION.ROUTE_CUSTOMIZE,
	Advanced: PERMISSION.ROUTE_ADVANCED,
	MenuGroups: PERMISSION.ROUTE_MENU_GROUPS,
	Subsidies: PERMISSION.ROUTE_SUBSIDIES,
	Settings: PERMISSION.ROUTE_SETTINGS,
	ReplicateMenus: PERMISSION.ROUTE_REPLICATE,
	MultiLocationSettings: PERMISSION.ROUTE_MULTI_LOCATION_SETTINGS,
	UserManagement: PERMISSION.ROUTE_MULTI_LOCATION_SETTINGS,
	UserProfile: PERMISSION.ROUTE_MULTI_LOCATION_SETTINGS,
	BulkEditMenu: PERMISSION.ROUTE_MULTI_LOCATION_BULK_EDIT,
	BulkEditSettings: PERMISSION.ROUTE_MULTI_LOCATION_BULK_EDIT,
	RallyIntelligence: PERMISSION.ADMIN_ONLY,
	OrderManagement: PERMISSION.ROUTE_ORDER_MANAGEMENT,
	OrdersStatusScreen: PERMISSION.ORDERS_STATUS_SCREEN,
	Dashboard: PERMISSION.ROUTE_DASHBOARD,
	Credits: PERMISSION.ROUTE_CREDITS,
	Events: PERMISSION.ROUTE_EVENTS,
	AllEvents: PERMISSION.ROUTE_EVENTS,
	EventSettings: PERMISSION.ROUTE_EVENTS,
	EventDetails: PERMISSION.ROUTE_EVENTS,
	SuiteAssignments: PERMISSION.ROUTE_EVENTS,
	SuiteSettings: PERMISSION.ROUTE_EVENTS,
	Beo: PERMISSION.ROUTE_EVENTS,
	ProductionSheet: PERMISSION.ROUTE_EVENTS,
	EventDetailsEdit: PERMISSION.ROUTE_EVENTS,
	CreateEvent: PERMISSION.ROUTE_EVENTS,
	SuiteOwners: PERMISSION.ROUTE_EVENTS,
	EventTypes: PERMISSION.ROUTE_EVENTS,
	Inquiries: PERMISSION.ROUTE_INQUIRIES,
	CateringBeo: PERMISSION.ROUTE_CATERING_BEO
};

/**
 * Make sure the user is logged in before entering routes
 * Make sure the user has the role to access specific routes
 * Make sure the menu and/or sections exist before entering specific routes
 * Make sure the suites are enables before entering suites routes
 * Make sure the event and/or suite exist before entering specific routes
 *
 * @param {Route} to - route to go to
 * @param {Route} from - route user is from
 * @param {Route} next - allow to proceed to the route
 * @return {void}
 */
// eslint-disable-next-line max-lines-per-function
router.beforeResolve(async (to, from, next) => {
	let authenticated: boolean = Auth.loggedIn;

	// Silent login for B2B app
	if (window.B2BApp) {
		const accessToken: string | undefined = window.B2BApp.getAccessToken();
		const idToken: string | undefined = window.B2BApp.getIdToken();
		const employeeId: string | undefined = window.B2BApp.getEmployeeId?.();
		const employeeName: string | undefined = window.B2BApp.getEmployeeName?.() || undefined;
		const createdBy: string | undefined = window.B2BApp.getCreatedBy?.() || undefined;
		const pinRole: string | undefined = window.B2BApp.getPinRole?.();

		if(accessToken && idToken) {
			authenticated = await store.dispatch('auth/loginAppUser', { accessToken, idToken, employeeId, employeeName, createdBy, pinRole });
		}
	}

	if (!authenticated) {
		store.commit('auth/SET_AUTHENTICATED', false);
		store.dispatch('auth/clear');
		if (!['Login', 'LoginCallback'].includes(to.name ?? '')) {
			return next({ path: '/login', replace: true });
		}
		next();
	}
	else {
		const roleLevel = store.getters['auth/getRoleLevel'];
		const isPosIntegrated = store.getters['auth/isPosIntegrated'];
		store.commit('auth/SET_AUTHENTICATED', true);

		// If the restaurants list hasn't been populated yet (tab reload — we no longer persist it
		// in cookies/localStorage), refetch it from the Auth0 id_token metadata.
		if (!store.getters['auth/getRestaurants']) {
			await Auth.refreshRestaurantsFromToken();
		}

		// If user is already logged in, prevent them from going to the form
		if (['Login', 'LoginCallback'].includes(to.name ?? '')) {
			return next({ path: '/menus', replace: true });
		}

		// If the user is a staff member, they should only have access to the create order page
		if (hasPermission(PERMISSION.SESSION_STAFF, roleLevel, isPosIntegrated) && to.name !== 'CreateOrder') {
			return next({ path: '/order-management/create', replace: true });
		}

		if (hasPermission(PERMISSION.SESSION_KIOSK, roleLevel, isPosIntegrated)) {
			if (Boolean(to.params.order_id) && to.name !== 'EditOrder') {
				return next({ name: 'EditOrder', params: { order_id: to.params.order_id }, replace: true });
			}
			if (to.name !== 'EditOrder' && to.name !== 'CreateOrder') {
				return next({ name: 'CreateOrder', replace: true });
			}
		}

		// Role-based route access for every matrix route, resolved via the permission registry
		// (ROUTE_PERMISSIONS is defined once at module scope). Feature availability
		// (suites/credits/dashboard/etc. flags) is checked separately below.
		const routePermission: PERMISSION | undefined = ROUTE_PERMISSIONS[to.name ?? ''];
		if (routePermission && !hasPermission(routePermission, roleLevel, isPosIntegrated)) {
			return next({ path: '/menus', replace: true });
		}

		// The pull-all flow is restricted to enterprise users and above
		if (!hasPermission(PERMISSION.ROUTE_ENTERPRISE_MENU_PULL, roleLevel, isPosIntegrated) &&
			['EnterpriseMenuPull'].includes(to.name ?? '')) {
			return next({ path: '/locations', replace: true });
		}

		// If menus not fetched, fetch them and wait
		// Don't fetch menus if user is a staff member, they only have access to the create order page which already fetches menus
		if (!store.getters['menus/getFetchMenusComplete'] && !hasPermission(PERMISSION.SESSION_STAFF, roleLevel, isPosIntegrated)) {
			await store.dispatch('menus/fetchMenus')
				.catch((errorMessage: string) => {
					store.commit('menus/SET_FETCH_MENUS_ERROR', errorMessage);
				});
		}
		else {
			// Set fetch menus complete to true, to prevent inifinite loading spinner for staff
			store.commit('menus/SET_FETCH_MENUS_COMPLETE', true);
		}

		// Guard to paths with menu and/or section ids
		if (['Sections', 'MenuItems', 'SectionItems'].includes(to.name ?? '')) {
			if (to.params.menu_id) {
				const menu = store.getters['menus/getMenu'](+to.params.menu_id);

				// If path has menu_id but menu doesn't exist, redirect to not found
				if (!menu) {
					return next({ path: '/not-found', replace: true });
				}
				// If path also has section_id, check section exists
				if (menu && to.params.section_id) {
					const section = store.getters['sections/getSection'](+to.params.menu_id, +to.params.section_id);
					if (!section) {
						return next({ path: '/not-found', replace: true });
					}
				}
			}
		}

		// Events pages require suites to be enabled (role is gated by the registry map above).
		if (!store.getters['auth/areSuitesEnabled'] &&
			['Events', 'AllEvents', 'EventSettings', 'EventDetails', 'SuiteAssignments', 'SuiteSettings', 'Beo', 'ProductionSheet', 'EventDetailsEdit', 'CreateEvent', 'SuiteOwners', 'EventTypes'].includes(to.name ?? '')) {
			return next({ path: '/menus', replace: true });
		}

		// Credits page requires credits to be enabled (role is gated by the registry map above).
		if (!store.getters['auth/areApp8CreditsEnabled'] && to.name === 'Credits') {
			return next({ path: '/menus', replace: true });
		}

		// Rally Intelligence page requires the feature flag (role is gated by the registry map above).
		if (!store.getters['auth/isRallyIntelligenceEnabled'] && to.name === 'RallyIntelligence') {
			return next({ path: '/menus', replace: true });
		}

		// If accessing inquiries or BEO page while catering isn't enabled, redirect to menus
		if (['Inquiries', 'CateringBeo'].includes(to.name ?? '') && !store.getters['auth/areCateringEventsEnabled']) {
			return next({ path: '/menus', replace: true });
		}

		// Only catering and k12 locations can access the orders report page
		if (to.name === 'OrdersReport' && !(store.getters['auth/isCateringRestaurant'] || store.getters['auth/isK12Location'])) {
			return next({ path: '/menus', replace: true });
		}

		// Only marketplace hubs or locations with the order_management flag can access the order management page
		if (to.name === 'OrderManagement' && !store.getters['auth/isOrderManagementEnabled'] && !store.getters['auth/isMarketplaceEnabled']) {
			return next({ path: '/menus', replace: true });
		}

		// Only locations with the orders_status_screen flag, and not marketplace hubs, can access the order status screen page
		if (to.name === 'OrdersStatusScreen' && (store.getters['auth/isMarketplaceEnabled'] || !store.getters['auth/getRestaurant'].menu_manager_config?.orders_status_screen)) {
			return next({ path: '/menus', replace: true });
		}

		// Only locations with the dashboard flag can access the dashboard page
		// K12 locations cannot access the dashboard page for the time being, as it will require more work to optimize it for the larger volume of orders they receive
		if ((!store.getters['auth/isDashboardEnabled'] || store.getters['auth/isK12Location']) && to.name === 'Dashboard') {
			return next({ path: '/menus', replace: true });
		}

		// Guard for pages with a event_id and/or suite_id
		if (from.name === null && ['EventDetails', 'SuiteAssignments', 'SuiteSettings', 'Beo', 'ProductionSheet', 'EventDetailsEdit'].includes(to.name ?? '')) {
			if (to.params.event_id) {
				// If no events, fetch and wait
				if (!store.getters['events/getEvents'].length) {
					await store.dispatch('events/fetchEvents');
				}

				// If no event, redirect to not found
				const event: SuitesEvent|undefined = store.getters['events/getEvent'](+to.params.event_id);
				if (!event) {
					return next({ path: '/not-found', replace: true });
				}

				if (event && to.params.suite_id) {
					// If suite_id is not in event_suites array of that event, redirect to not found
					const eventSuite = event.event_suites.find(suite => suite.id === +to.params.suite_id);
					if (!eventSuite) {
						return next({ path: '/not-found', replace: true });
					}
				}
			}
		}

		next();
	}
});

// Register hooks for route navigation guards
Component.registerHooks([
	'beforeRouteEnter',
	'beforeRouteLeave',
	'beforeRouteUpdate'
]);

new Vue({
	vuetify,
	router,
	store,
	i18n,
	render: h => h(App)
}).$mount('#menu-management');
